/* eslint-disable @typescript-eslint/naming-convention */
import Channel, { ChannelDestination } from './Channel';
import Handler from './Handler';
import IndigoApiError from './IndigoApiError';
import IndigoLoginError, { IndigoLoginErrorType } from './IndigoLoginError';
import NetworkState from 'communication/NetworkState';
import SockJSStreamingService, {
    RequestBatchSanitizer,
} from './streaming/SockJSStreamingService';
import LoginResponse from './LoginResponse';
import LayoutShare from './types/layoutShare/LayoutShare';
import LayoutWithShares from './types/layoutShare/LayoutWithShares';
import { Currency } from 'app/currencies/store/CurrencyState';
import {
    ContactSupportRequest,
    ContactSupportRequestState,
} from './types/ContactSupportRequest';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import UIConfigurationDto from './types/uiConfiguration/UIConfigurationDto';
import IndigoUser from './types/IndigoUser';
import IndigoMalformedMessage from 'communication/IndigoMalformedMessage';
import { ClickstreamEvent } from 'clickstream/ClickstreamService';
import {
    ApiRequestOptions,
    nullApiRequestOptions,
} from 'communication/ApiCallInfo';
import IndigoServiceClient, {
    encodeUrl,
} from 'communication/IndigoServiceClient';
import { getGlobalWindowVariables } from 'utils/globalWindowVariables';
import { AxiosError } from 'axios';
import { NotificationService } from 'notification/NotificationService';
import { ChannelMessageStorageManager } from 'communication/streaming/ChannelMessageStorageManager';
import StreamingService, {
    StreamingServiceInfo,
} from './streaming/StreamingService';
import platform from 'platform';
import { v4 as uuid } from 'uuid';

export interface IndigoApiOptions {
    loggingService: LoggingService;
    notificationService: NotificationService;
    indigoServiceClient: IndigoServiceClient;
}

export interface JwtLoginOptions {
    token: string;
    application: string;
    machineId: string | undefined;
    applicationVersion: string | undefined;
    correlationid: string | undefined;
}

export type NetworkChangeSubscriber = (networkState: NetworkState) => void;
export type AvailabilityStatusChangeSubscriber = (status: {
    availabilityStatus: string;
    available: boolean;
}) => void;
export type UnhandledMalformedMessageSubscriber = (
    message: IndigoMalformedMessage
) => void;

export default class IndigoApi {
    private channelMessageStorageManager = new ChannelMessageStorageManager();
    private readonly streamingService: StreamingService;
    private indigoServiceClient: IndigoServiceClient;
    private notificationService: NotificationService;
    public status?: NetworkState;
    private _userDetails?: LoginResponse = undefined;
    private loggingService: LoggingService;
    private logger: Logger;
    private networkChangeSubscribers: NetworkChangeSubscriber[];
    private availabilityStatusChangeSubscribers: AvailabilityStatusChangeSubscriber[];
    private unhandledMalformedMessageSubscribers: UnhandledMalformedMessageSubscriber[];
    private requestSanitizers: {
        [channelDestination: string]: RequestBatchSanitizer;
    } = {};
    private connectingWebSocketPromise: Promise<void>;
    private onStreamingServiceReconnectedToIndigoCb: (() => void) | undefined;

    public constructor(options: IndigoApiOptions) {
        this.loggingService = options.loggingService;
        this.indigoServiceClient = options.indigoServiceClient;
        this.notificationService = options.notificationService;
        this.logger = buildLogger('IndigoApi', this.loggingService);
        this.networkChangeSubscribers = [];
        this.availabilityStatusChangeSubscribers = [];
        this.unhandledMalformedMessageSubscribers = [];

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const indigoApi: IndigoApi = this;
        this.streamingService = new SockJSStreamingService({
            onNetworkStateUpdate: (networkState: NetworkState): void => {
                this.onNetworkStateUpdate(networkState);
            },
            onAvailabilityStatusUpdate: (status: {
                availabilityStatus: string;
                available: boolean;
            }): void => {
                this.onAvailabilityStatusUpdate(status);
            },
            onUnhandledMalformedMessage: (
                message: IndigoMalformedMessage
            ): void => {
                this.onUnhandledMalformedMessage(message);
            },
            onReconnectedToIndigo:
                this.onStreamingServiceReconnectedToIndigo.bind(this),
            loggingService: this.loggingService,
            indigoApi,
            indigoServiceClient: this.indigoServiceClient,
            requestSanitizers: this.requestSanitizers,
            notificationService: this.notificationService,
            channelMessageStorageManager: this.channelMessageStorageManager,
        });
        this.connectingWebSocketPromise = this.connectWebSocket.bind(this)();
    }

    private get userDetails(): LoginResponse | undefined {
        return this._userDetails;
    }
    private set userDetails(value: LoginResponse | undefined) {
        this._userDetails = value;
        if (value) {
            this.indigoServiceClient.connectionId = value.connectionId;
            this.indigoServiceClient.sessionId = value.uid;
        }
    }

    private static runHandlingErrors<K>(
        description: string,
        block: () => K
    ): K {
        try {
            return block();
        } catch (error) {
            throw new IndigoApiError(description, error);
        }
    }

    public subscribeNetworkChange(subscriber: NetworkChangeSubscriber) {
        this.networkChangeSubscribers.push(subscriber);
    }

    public subscribeAvailabilityStatusChange(
        subscriber: AvailabilityStatusChangeSubscriber
    ) {
        this.availabilityStatusChangeSubscribers.push(subscriber);
    }

    public subscribeUnhandledMalformedMessage(
        subscriber: UnhandledMalformedMessageSubscriber
    ) {
        this.unhandledMalformedMessageSubscribers.push(subscriber);
    }

    public async checkReady(options: ApiRequestOptions): Promise<void> {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Check Ready`,
            async () => {
                return await this.indigoServiceClient.get<void>(
                    `noauth/ready`,
                    options
                );
            }
        );
    }

    ////////////////////////////////////////////////////////////////
    //
    // AuthService
    //
    ////////////////////////////////////////////////////////////////

    public async login(
        username: string,
        password: string,
        options: ApiRequestOptions
    ): Promise<LoginResponse> {
        if (!username || !password) {
            throw new IndigoLoginError(
                IndigoLoginErrorType.MISSING_CREDENTIALS_LOGIN_ERROR
            );
        }
        // For login (standalone), we retrieve a client-generated machine id and include it in the remote login request...
        const getMachineIdFn = getGlobalWindowVariables().getMachineId;
        let machineId: string | undefined = undefined;
        const { application, applicationVersion } = getGlobalWindowVariables();
        try {
            machineId = getMachineIdFn && getMachineIdFn();
            if (!machineId) {
                this.logger.critical('Machine id not found');
            } else {
                this.logger.info('Machine Id: ' + machineId);
            }
        } catch (error) {
            this.logger.critical('Error retrieving machine id');
        }
        this.indigoServiceClient.machineId = machineId;
        this.indigoServiceClient.application = application;
        this.indigoServiceClient.panelId = getGlobalWindowVariables().panelId;

        const applicationVersionHeader =
            applicationVersion || platform.description;
        if (applicationVersionHeader) {
            options.headers = options.headers ?? {};
            options.headers.ApplicationVersion = applicationVersionHeader;
        }

        try {
            this.userDetails =
                await this.indigoServiceClient.postCredentials<LoginResponse>(
                    'auth/login',
                    username,
                    password,
                    options
                );
        } catch (error) {
            const axiosError = error as AxiosError<{ errorCode: number }>;
            this.logger.warn('Error logging in', error);

            if (!error || !axiosError.response || !axiosError.response.status) {
                throw new IndigoLoginError(
                    IndigoLoginErrorType.GENERIC_LOGIN_ERROR,
                    error
                );
            }

            switch (axiosError.response.status) {
                case 401:
                    throw new IndigoLoginError(
                        IndigoLoginErrorType.INVALID_CREDENTIALS_LOGIN_ERROR
                    );
                case 403:
                    if (axiosError.response.data.errorCode === 9) {
                        throw new IndigoLoginError(
                            IndigoLoginErrorType.RESTRICTED_IP_LOGIN_ERROR
                        );
                    }
                    throw new IndigoLoginError(
                        IndigoLoginErrorType.NOT_AUTHORIZED_LOGIN_ERROR
                    );
                default:
                    throw new IndigoLoginError(
                        IndigoLoginErrorType.GENERIC_LOGIN_ERROR,
                        error
                    );
            }
        }

        try {
            await this.initialiseWebSocketSession();
        } catch (error) {
            throw new IndigoLoginError(
                IndigoLoginErrorType.GENERIC_LOGIN_ERROR,
                error
            );
        }

        return this.userDetails;
    }

    public async loginKey(
        sessionKey: string,
        options: ApiRequestOptions
    ): Promise<LoginResponse> {
        return await IndigoApiError.runHandlingErrorsAsync(
            'Login Key',
            async () => {
                const response =
                    await this.indigoServiceClient.postRawText<LoginResponse>(
                        'auth/loginKey',
                        sessionKey,
                        options
                    );
                // For loginKey (Newport panel) we extract the machine id (provided by Newport in the UpdateParms request '_ip' parameter)
                // from the remote loginKey response...
                this.indigoServiceClient.machineId = response.machineId;
                const application: string | undefined =
                    getGlobalWindowVariables().application;
                this.indigoServiceClient.application = application;
                this.indigoServiceClient.panelId =
                    getGlobalWindowVariables().panelId;
                this.userDetails = response;
                await this.initialiseWebSocketSession();
                return this.userDetails!;
            }
        );
    }

    public async loginJwt(
        jwtLoginOptions: JwtLoginOptions,
        options: ApiRequestOptions
    ): Promise<LoginResponse> {
        const {
            token,
            application,
            applicationVersion,
            machineId: machineIdFromOptions,
            correlationid,
        } = jwtLoginOptions;

        const auth = `Bearer ${token}`;
        options.headers = options.headers ?? {};
        options.headers.Authorization = auth;
        options.headers.Application = application;
        this.indigoServiceClient.application = application;
        this.indigoServiceClient.panelId = getGlobalWindowVariables().panelId;
        options.headers.ApplicationVersion = applicationVersion;
        options.headers.CorrelationId = correlationid ?? uuid();

        const getMachineIdFn = getGlobalWindowVariables().getMachineId;

        const machineId = machineIdFromOptions
            ? machineIdFromOptions
            : getMachineIdFn
            ? getMachineIdFn()
            : undefined;
        if (machineId) {
            options.headers.MachineId = machineId;
            this.indigoServiceClient.machineId = machineId;
        }

        return await IndigoApiError.runHandlingErrorsAsync(
            'Login Jwt',
            async () => {
                const response = await this.indigoServiceClient.post<
                    unknown,
                    LoginResponse
                >('auth/login', undefined, options);
                this.userDetails = response;
                await this.initialiseWebSocketSession();
                return this.userDetails!;
            }
        );
    }

    public async logout(): Promise<void> {
        return await IndigoApiError.runHandlingErrorsAsync(
            'Logout',
            async () => {
                await this.indigoServiceClient.post<
                    { connectionId: number },
                    void
                >('auth/logout', {
                    connectionId: this.userDetails!.connectionId,
                });
                this.streamingService!.clearSession();
                await this.indigoServiceClient.clearSession();
            }
        );
    }

    ////////////////////////////////////////////////////////////////
    //
    // UIConfigService
    //
    ////////////////////////////////////////////////////////////////

    public async saveUIConfiguration(
        uiConfig: UIConfigurationDto,
        options: ApiRequestOptions
    ) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Save Layout`,
            async () => {
                return await this.indigoServiceClient.post<
                    UIConfigurationDto,
                    UIConfigurationDto
                >(`layout`, uiConfig, options);
            }
        );
    }

    public async loadUIConfiguration(
        layoutName: string,
        origin: string,
        globalVersion: boolean,
        options: ApiRequestOptions
    ): Promise<UIConfigurationDto> {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Load UI Configuration`,
            async () => {
                const url = encodeUrl`layout/${origin}/${layoutName}`;
                const queryParameters = globalVersion
                    ? { globalOnly: 'true' }
                    : undefined;
                return await this.indigoServiceClient.get<UIConfigurationDto>(
                    url,
                    options,
                    queryParameters
                );
            }
        );
    }

    public async deleteUIConfiguration(id: number, options: ApiRequestOptions) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Delete UI Configuration`,
            async () => {
                return await this.indigoServiceClient.delete(
                    `layout/${id}`,
                    options
                );
            }
        );
    }

    public async panelConfigurationLoad(
        layoutName: string,
        origin: string,
        panelId: string,
        options: ApiRequestOptions
    ): Promise<UIConfigurationDto> {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Load UI Panel Configuration`,
            async () => {
                const url = encodeUrl`layout/panelLoad/${origin}/${layoutName}/${panelId}`;
                return await this.indigoServiceClient.post<
                    undefined,
                    UIConfigurationDto
                >(url, undefined, options);
            }
        );
    }

    public async panelConfigurationSwitch(
        layoutName: string,
        origin: string,
        panelId: string,
        options: ApiRequestOptions
    ): Promise<UIConfigurationDto> {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Switch UI Panel Configuration`,
            async () => {
                const url = encodeUrl`layout/panelSwitch/${origin}/${layoutName}/${panelId}`;
                return await this.indigoServiceClient.post<
                    undefined,
                    UIConfigurationDto
                >(url, undefined, options);
            }
        );
    }

    ////////////////////////////////////////////////////////////////
    //
    // LayoutSharingService
    //
    ////////////////////////////////////////////////////////////////

    public async createNewLayoutShare(
        layoutBeingShared: LayoutShare,
        options: ApiRequestOptions
    ) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Save Layout Share`,
            async () => {
                return await this.indigoServiceClient.post<LayoutShare, void>(
                    `layout/share`,
                    layoutBeingShared,
                    options
                );
            }
        );
    }

    public async duplicateLayout(
        layoutId: number,
        newLayoutName: string,
        options: ApiRequestOptions
    ) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Duplicate Layout Layout`,
            async () => {
                const url = encodeUrl`layout/copy/${layoutId.toString()}/${newLayoutName}`;
                return await this.indigoServiceClient.post<
                    undefined,
                    UIConfigurationDto
                >(url, undefined, options);
            }
        );
    }

    public async getAllLayoutsWithSharesForUserAndOrigin(
        origin: string,
        includeGlobal: boolean,
        options: ApiRequestOptions
    ) {
        const queryParameters: {
            [key: string]: string;
        } = {};
        if (includeGlobal) {
            queryParameters.includeGlobal = 'true';
        }
        return await IndigoApiError.runHandlingErrorsAsync(
            `Get All Layouts With Shares For User And Origin`,
            async () => {
                return await this.indigoServiceClient.get<LayoutWithShares[]>(
                    `layout/layoutsWithShares/${origin}`,
                    options,
                    queryParameters
                );
            }
        );
    }

    public async getAllSharesForUser(options: ApiRequestOptions) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Get All Shares For User`,
            async () => {
                return await this.indigoServiceClient.get<LayoutShare[]>(
                    `layout/share`,
                    options
                );
            }
        );
    }

    public async getAllSharesForLayout(
        layoutId: number,
        options: ApiRequestOptions
    ) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Get All Shares For Layout`,
            async () => {
                return await this.indigoServiceClient.get<LayoutShare[]>(
                    `layout/${layoutId}/share`,
                    options
                );
            }
        );
    }

    public async getShareUsers(options: ApiRequestOptions) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `Get all users available to share config with`,
            async () => {
                return await this.indigoServiceClient.get<IndigoUser[]>(
                    `layout/shareUsers`,
                    options
                );
            }
        );
    }

    // Currencies

    public async loadCurrencies(
        currencySymbols: string[],
        options: ApiRequestOptions
    ) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `GetCurrencies`,
            async () => {
                return await this.indigoServiceClient.post<
                    Record<string, unknown>,
                    Currency[]
                >(
                    `marketdata/currency/${currencySymbols.join(',')}`,
                    {},
                    options
                );
            }
        );
    }

    // Contact Support

    public async postContactSupportRequest(
        enquiry: string,
        state: ContactSupportRequestState,
        machineId?: string,
        forDevTeam?: boolean
    ) {
        const headers = machineId ? { MachineId: machineId } : {};
        const options: ApiRequestOptions = {
            ...nullApiRequestOptions,
            headers,
        };
        return await IndigoApiError.runHandlingErrorsAsync(
            `ContactSupport`,
            async () => {
                return await this.indigoServiceClient.post<
                    ContactSupportRequest,
                    void
                >(`supportRequest`, { enquiry, state, forDevTeam }, options);
            }
        );
    }

    ////////////////////////////////////////////////////////////////
    //
    // Clickstream
    //
    ////////////////////////////////////////////////////////////////

    public async postClickstreamEvent<TEvent>(
        clickstreamEvent: ClickstreamEvent<TEvent>,
        options: ApiRequestOptions
    ) {
        return await IndigoApiError.runHandlingErrorsAsync(
            `postClickstreamEvent`,
            async () => {
                return await this.indigoServiceClient.post<
                    ClickstreamEvent<TEvent>,
                    void
                >(`clickstreamEvent`, clickstreamEvent, options);
            }
        );
    }

    ////////////////////////////////////////////////////////////////
    //
    // Streaming
    //
    ////////////////////////////////////////////////////////////////

    public async pause(): Promise<void> {
        return await IndigoApiError.runHandlingErrorsAsync(
            `pause`,
            async () => {
                return await this.indigoServiceClient.post<
                    { connectionId: number },
                    void
                >(`stream/pause`, {
                    connectionId: this.userDetails!.connectionId,
                });
            }
        );
    }

    ////////////////////////////////////////////////////////////////
    //
    // Internal
    //
    ////////////////////////////////////////////////////////////////

    public async rpcRequest<T, V>(
        requestPayload: T,
        destination: ChannelDestination
    ): Promise<V> {
        return IndigoApi.runHandlingErrors(`RPC Request`, () => {
            return this.streamingService.rpcRequest(
                requestPayload,
                destination
            );
        });
    }

    public startStreamRequest<T, V>(
        requestPayload: T,
        indigo2AsyncDestination: ChannelDestination,
        handler: Handler<V>,
        channelId?: number
    ): Channel<T, V> {
        return IndigoApi.runHandlingErrors(`Start Stream Request`, () => {
            return this.streamingService.startStreamRequest(
                requestPayload,
                indigo2AsyncDestination,
                handler,
                channelId
            );
        });
    }

    public stopDataStream(
        channelId: number,
        indigo2AsyncDestination: ChannelDestination,
        removeChannelListener = true
    ): void {
        return IndigoApi.runHandlingErrors(`Stop Data Stream`, () => {
            return this.streamingService.stopDataStream(
                channelId,
                indigo2AsyncDestination,
                removeChannelListener
            );
        });
    }

    public getStreamInfo(): StreamingServiceInfo | undefined {
        if (!this.streamingService) {
            return undefined;
        }
        return this.streamingService.getInfo();
    }

    private async connectWebSocket() {
        this.logger.debug('Connecting web socket');
        try {
            await this.streamingService.connect();
            this.logger.info('Connected to webSocket');
        } catch (e) {
            this.logger.critical('Error connecting to webSocket', e);
        }
    }

    public registerStreamingServiceReconnectedToIndigoCallback(cb: () => void) {
        this.onStreamingServiceReconnectedToIndigoCb = cb;
    }

    public beginStreamRequestBatch() {
        this.streamingService && this.streamingService.startRequestBatch();
    }

    public commitStreamRequestBatch() {
        this.streamingService && this.streamingService.commitRequestBatch();
    }

    private onUnhandledMalformedMessage(message: IndigoMalformedMessage): void {
        for (const subscriber of this.unhandledMalformedMessageSubscribers) {
            subscriber(message);
        }
    }

    private onNetworkStateUpdate(networkState: NetworkState): void {
        this.status = networkState;
        for (const subscriber of this.networkChangeSubscribers) {
            subscriber(networkState);
        }
    }

    private async initialiseWebSocketSession(): Promise<void> {
        const { session, connectionId, uid } = this.userDetails!;

        await this.connectingWebSocketPromise;
        this.streamingService.initSession({ connectionId, session, uid });

        // initialize internal channel listener
        this.streamingService.initialiseInternalChannel(this.userDetails!);
    }

    public registerRequestBatchSanitizer(
        channelDestination: ChannelDestination,
        sanitizer: RequestBatchSanitizer
    ) {
        this.requestSanitizers[channelDestination] = sanitizer;
        if (this.streamingService) {
            this.streamingService!.registerRequestBatchSanitizer(
                channelDestination,
                sanitizer
            );
        }
    }

    private onAvailabilityStatusUpdate(status: {
        availabilityStatus: string;
        available: boolean;
    }) {
        for (const subscriber of this.availabilityStatusChangeSubscribers) {
            subscriber(status);
        }
    }

    private onStreamingServiceReconnectedToIndigo() {
        if (this.onStreamingServiceReconnectedToIndigoCb) {
            this.onStreamingServiceReconnectedToIndigoCb();
        }
    }
}
